import {
  DigitalCardSettingsData,
  EmployeeWithBrand,
  WalletSettingsData,
} from "./card-digital.interface";

export interface DigitalCardProfileItem {
  profileId: string;
  displayName: string;
  status: string;
  isDefault: boolean;
  currentRenderId: string | null;
  email?: string | null;
  createdAt: string;
  publicUrl?: string;
}

export interface DigitalCardProfilesResponse {
  profiles: DigitalCardProfileItem[];
}

export interface DigitalCardProfileDetail {
  profileId: string;
  displayName: string;
  status: string;
  isDefault: boolean;
  currentRenderId: string | null;
  email: string | null;
  settings: DigitalCardSettingsData;
  walletSettings?: WalletSettingsData;
  publicUrl?: string;
  employee?: EmployeeWithBrand;
}

export interface DigitalCardProfileAnalytics {
  profileId: string;
  displayName: string;
  email?: string | null;
  byElement: { element: string; count: number }[];
  byRender: { renderId: string; count: number }[];
  bySource: { token: string; medium: string; count: number }[];
  totalLandings: number;
  totalInteractions: number;
  totalSourceHits: number;
  totalPreviews: number;
}

export interface DigitalCardDomainAnalytics {
  summary: {
    totalLandings: number;
    totalInteractions: number;
    totalSourceHits: number;
    totalPreviews: number;
    totalSavedContacts?: number;
  };
  columns: {
    mediums: string[];
    mediumLabels?: Record<string, string>;
    savedContactForm: boolean;
    elements: string[];
  };
  overview?: {
    cards: number;
    sources: { key: string; count: number }[];
    elements: { key: string; count: number }[];
    savedContacts: number;
    sourceHits?: number;
    interactions?: number;
    visits?: number;
  };
  data: {
    employee: string;
    cardName: string;
    visits: number;
    mediumCounts: Record<string, number>;
    savedContactForms: number;
    elementCounts: Record<string, number>;
  }[];
  total: number;
}

export interface DigitalCardCustomSourceItem {
  id: string;
  name: string;
  medium: string;
  createdAt: string;
  totalHits: number;
}

export interface DigitalCardSourceShareDialogData {
  sourceId: string;
  sourceName: string;
  medium: string;
}

export interface DigitalCardProfileReadiness {
  status: "pending" | "ready";
  expected?: number;
  synced?: number;
  updatedAt?: number;
  error?: string;
}

export interface DigitalCardSettingsResponse {
  id?: string;
  renderId?: string;
  domain?: string;
  settings?: unknown;
}

export interface DigitalCardsState {
  profiles: DigitalCardProfileItem[] | null;
  loading: boolean;
  error?: unknown;
  employeeProfilesByTemplateId: Record<string, DigitalCardProfileItem[]>;
  loadingEmployeeTemplateId: string | null;
  templateReadinessById: Record<string, DigitalCardProfileReadiness>;
}

export interface DigitalCardEntryDialogData {
  profileId: string;
  name: string;
  email?: string | null;
  mine?: boolean;
}

export const SHARE_MEDIUMS = [
  "share",
  "qr",
  "qr_paper",
  "qr_plastic",
  "nfc",
  "nfc_paper",
  "nfc_plastic",
  "email",
  "whatsapp",
  "facebook",
  "x",
  "linkedin",
] as const;

export type SourceMedium = (typeof SHARE_MEDIUMS)[number];

export interface DigitalCardShareDialogData {
  shareUrl: string;
  displayName: string;
  profileId: string;
  email?: string | null;
}

export interface DigitalCardVariablePaletteDialogData {
  onInsertVariable: (text: string) => void;
}

export type DomainStatsRow = DigitalCardDomainAnalytics["data"][number];

export type PickerQuery = { phrase: string | undefined; refreshSeq: number };

export const ICON_KEYS = [
  "phone",
  "mobile",
  "email",
  "www",
  "calendar",
  "behance",
  "coffee",
  "facebook",
  "github",
  "gitlab",
  "gitter",
  "heart",
  "instagram",
  "linkedin",
  "mastodon",
  "menu",
  "ok",
  "reddit",
  "skype",
  "slack",
  "stackoverflow",
  "telegram",
  "vk",
  "whatsapp",
  "x",
  "xing",
  "youtube",
  "manager",
  "jobTitle",
  "department",
  "branchName",
  "branchAddress",
  "workAddress",
  "customLink",
] as const;

export type IconKey = (typeof ICON_KEYS)[number];

export interface HardcodedVariableIcon {
  key: IconKey;
  label: string;
  value: string;
  preview: string;
}
